import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '#environments';
import {
  CancelRLCaseRequest,
  CreateRLCaseRequest,
  CreateRLLookupOptionRequest,
  CreateRLRigRequest,
  LinkWellviewRequest,
  RLAttachmentMetadata,
  RLAttachmentUrl,
  RLAuditLogEntry,
  RLCaseDetail,
  RLCaseListQuery,
  RLCasePage,
  RLExecutionView,
  RLNotificationLogEntry,
  PlantCodeOption,
  RLLookupOption,
  RLRig,
  RLRigQueueRow,
  UpdateRigQueueItem,
  RLWellSearchResult,
  SaveRLJobPlanRequest,
  ScheduleRLCaseRequest,
  SendBackRLCaseRequest,
  UpdateRLCloseOutRequest,
  UpdateRLExecutionRequest,
  UpdateRLLookupOptionRequest,
  UpdateRLPlanningRequest,
  UpdatePreScheduleStatusRequest,
  UpdateRLReviewRequest,
  UpdateRLRigRequest,
  WellViewJobResponse,
  WellviewJobTimeLogEnvelopeDto,
} from '#models';

@Injectable({
  providedIn: 'root',
})
export class RLWorkflowApiService {
  private _http = inject(HttpClient);
  private readonly _baseUrl = environment.apiUrl;

  // ─── RL Cases ──────────────────────────────────────────────────────────────

  getCases(query?: RLCaseListQuery): Observable<RLCasePage> {
    let params = new HttpParams();
    if (query?.page != null) params = params.set('page', query.page);
    if (query?.pageSize != null) params = params.set('pageSize', query.pageSize);
    if (query?.stage) params = params.set('stage', query.stage);
    if (query?.sort) params = params.set('sort', query.sort);
    if (query?.sortDescending != null) params = params.set('sortDescending', query.sortDescending);
    if (query?.keyword) params = params.set('keyword', query.keyword);
    if (query?.excludeSentToJobPlan != null) params = params.set('excludeSentToJobPlan', query.excludeSentToJobPlan);
    if (query?.onlySentToJobPlan != null) params = params.set('onlySentToJobPlan', query.onlySentToJobPlan);
    return this._http.get<RLCasePage>(`${this._baseUrl}/rl-cases`, { params });
  }

  createCase(request: CreateRLCaseRequest): Observable<RLCaseDetail> {
    return this._http.post<RLCaseDetail>(`${this._baseUrl}/rl-cases`, request);
  }

  getCaseById(id: number): Observable<RLCaseDetail> {
    return this._http.get<RLCaseDetail>(`${this._baseUrl}/rl-cases/${id}`);
  }

  getCaseByCaseNumber(caseNumber: string): Observable<RLCaseDetail> {
    return this._http.get<RLCaseDetail>(`${this._baseUrl}/rl-cases/by-number/${encodeURIComponent(caseNumber)}`);
  }

  updatePlanning(id: number, request: UpdateRLPlanningRequest): Observable<RLCaseDetail> {
    return this._http.put<RLCaseDetail>(`${this._baseUrl}/rl-cases/${id}/planning`, request);
  }

  /**
   * Creates the RLCase row (if not yet present) for the given shared Cases.Id and upserts
   * RLPlanningData. Used by the legacy Planning page before a dedicated RLCase record exists.
   */
  savePlanningByBaseCase(baseCaseId: number, request: UpdateRLPlanningRequest): Observable<RLCaseDetail> {
    return this._http.put<RLCaseDetail>(`${this._baseUrl}/rl-cases/by-base/${baseCaseId}/planning`, request);
  }

  saveJobPlan(id: number, request: SaveRLJobPlanRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/rl-cases/${id}/job-plan`, request);
  }

  submitCase(id: number): Observable<RLCaseDetail> {
    return this._http.put<RLCaseDetail>(`${this._baseUrl}/rl-cases/${id}/submit`, {});
  }

  revertJobPlan(id: number): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/rl-cases/${id}/revert-job-plan`, {});
  }

  updateReview(id: number, request: UpdateRLReviewRequest): Observable<RLCaseDetail> {
    return this._http.put<RLCaseDetail>(`${this._baseUrl}/rl-cases/${id}/review`, request);
  }

  updatePreScheduleStatus(id: number, request: UpdatePreScheduleStatusRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/rl-cases/${id}/pre-schedule-status`, request);
  }

  linkWellview(id: number, request: LinkWellviewRequest): Observable<RLCaseDetail> {
    return this._http.put<RLCaseDetail>(`${this._baseUrl}/rl-cases/${id}/link-wellview`, request);
  }

  getWellViewJobs(api10: string): Observable<WellViewJobResponse[]> {
    return this._http.get<WellViewJobResponse[]>(`${this._baseUrl}/wellview-jobs/${encodeURIComponent(api10)}`);
  }

  refreshWellview(id: number, wellviewJobId: string): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/rl-cases/${id}/refresh-wellview`, { wellviewJobId });
  }

  getWellViewTimeLogs(wellViewJobId: string): Observable<WellviewJobTimeLogEnvelopeDto> {
    return this._http.get<WellviewJobTimeLogEnvelopeDto>(
      `${this._baseUrl}/wellview-jobs/${encodeURIComponent(wellViewJobId)}/timelogs`,
    );
  }

  saveSchedulingData(id: number, request: ScheduleRLCaseRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/rl-cases/${id}/scheduling-data`, request);
  }

  scheduleCase(id: number, request: ScheduleRLCaseRequest): Observable<RLCaseDetail> {
    return this._http.put<RLCaseDetail>(`${this._baseUrl}/rl-cases/${id}/schedule`, request);
  }

  getExecution(id: number): Observable<RLExecutionView> {
    return this._http.get<RLExecutionView>(`${this._baseUrl}/rl-cases/${id}/execution`);
  }

  updateExecution(id: number, request: UpdateRLExecutionRequest): Observable<RLCaseDetail> {
    return this._http.put<RLCaseDetail>(`${this._baseUrl}/rl-cases/${id}/execution`, request);
  }

  generateFinalReport(id: number): Observable<RLAttachmentMetadata> {
    return this._http.post<RLAttachmentMetadata>(`${this._baseUrl}/rl-cases/${id}/final-report`, {});
  }

  updateCloseOut(id: number, request: UpdateRLCloseOutRequest): Observable<RLCaseDetail> {
    return this._http.put<RLCaseDetail>(`${this._baseUrl}/rl-cases/${id}/closeout`, request);
  }

  completeCase(id: number): Observable<RLCaseDetail> {
    return this._http.put<RLCaseDetail>(`${this._baseUrl}/rl-cases/${id}/complete`, {});
  }

  cancelCase(id: number, request: CancelRLCaseRequest): Observable<RLCaseDetail> {
    return this._http.post<RLCaseDetail>(`${this._baseUrl}/rl-cases/${id}/cancel`, request);
  }

  sendBack(id: number, request: SendBackRLCaseRequest): Observable<RLCaseDetail> {
    return this._http.put<RLCaseDetail>(`${this._baseUrl}/rl-cases/${id}/send-back`, request);
  }

  reopenCloseOut(id: number): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/rl-cases/${id}/reopen-close-out`, {});
  }

  uploadAttachment(id: number, attachmentType: string, file: File): Observable<RLAttachmentMetadata> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('attachmentType', attachmentType);
    return this._http.post<RLAttachmentMetadata>(`${this._baseUrl}/rl-cases/${id}/attachments`, formData);
  }

  getAttachments(id: number): Observable<RLAttachmentMetadata[]> {
    return this._http.get<RLAttachmentMetadata[]>(`${this._baseUrl}/rl-cases/${id}/attachments`);
  }

  getAttachmentUrl(id: number, attachmentType: string): Observable<RLAttachmentUrl> {
    return this._http.get<RLAttachmentUrl>(`${this._baseUrl}/rl-cases/${id}/attachments/${attachmentType}`);
  }

  /**
   * Returns a time-limited SAS download URL for a specific attachment by its numeric id.
   * Required for attachment types that can have multiple rows per case (e.g. Correspondence),
   * where {@link getAttachmentUrl} (keyed by attachmentType) cannot disambiguate between them.
   */
  getAttachmentDownloadUrl(id: number, attachmentId: number): Observable<RLAttachmentUrl> {
    return this._http.get<RLAttachmentUrl>(`${this._baseUrl}/rl-cases/${id}/attachments/${attachmentId}/download-url`);
  }

  getArtifactDownloadUrl(artifactType: string): Observable<RLAttachmentUrl> {
    return this._http.get<RLAttachmentUrl>(`${this._baseUrl}/artifacts/${encodeURIComponent(artifactType)}/download`);
  }

  deleteAttachment(id: number, attachmentId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/rl-cases/${id}/attachments/${attachmentId}`);
  }

  getAuditLog(id: number): Observable<RLAuditLogEntry[]> {
    return this._http.get<RLAuditLogEntry[]>(`${this._baseUrl}/rl-cases/${id}/audit-log`);
  }

  getAuditLogByCaseNumber(caseNumber: string): Observable<RLAuditLogEntry[]> {
    return this._http.get<RLAuditLogEntry[]>(`${this._baseUrl}/rl-cases/by-number/${encodeURIComponent(caseNumber)}/audit-log`);
  }

  getNotificationLog(id: number): Observable<RLNotificationLogEntry[]> {
    return this._http.get<RLNotificationLogEntry[]>(`${this._baseUrl}/rl-cases/${id}/notification-log`);
  }

  // ─── Rigs ───────────────────────────────────────────────────────────────────

  getEligibleRigs(): Observable<RLRig[]> {
    return this._http.get<RLRig[]>(`${this._baseUrl}/rigs`);
  }

  getAllRigs(): Observable<RLRig[]> {
    return this._http.get<RLRig[]>(`${this._baseUrl}/rigs/all`);
  }

  /** Returns all RL PreScheduling cases assigned to the given rig, ordered by queue position. */
  getRigQueue(rigId: number): Observable<RLRigQueueRow[]> {
    return this._http.get<RLRigQueueRow[]>(`${this._baseUrl}/rigs/${rigId}/queue`);
  }

  /** Batch-updates QueueOrder in SchedulingData for all cases in the rig's queue. */
  updateRigQueue(rigId: number, items: UpdateRigQueueItem[]): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/rigs/${rigId}/queue`, items);
  }

  createRig(request: CreateRLRigRequest): Observable<RLRig> {
    return this._http.post<RLRig>(`${this._baseUrl}/rigs`, request);
  }

  updateRig(id: number, request: UpdateRLRigRequest): Observable<RLRig> {
    return this._http.put<RLRig>(`${this._baseUrl}/rigs/${id}`, request);
  }

  deleteRig(id: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/rigs/${id}`);
  }

  // ─── Plant Codes ────────────────────────────────────────────────────────────

  getPlantCodes(): Observable<PlantCodeOption[]> {
    return this._http.get<PlantCodeOption[]>(`${this._baseUrl}/plant-codes`);
  }

  // ─── Lookups ────────────────────────────────────────────────────────────────

  getLookupOptions(fieldKey: string, parentValue?: string): Observable<RLLookupOption[]> {
    let params = new HttpParams();
    if (parentValue) params = params.set('parentValue', parentValue);
    return this._http.get<RLLookupOption[]>(`${this._baseUrl}/lookup/${fieldKey}`, { params });
  }

  /** Admin: returns lookup options with row identity for CRUD management */
  getAdminLookupOptions(fieldKey: string): Observable<RLLookupOption[]> {
    return this._http.get<RLLookupOption[]>(`${this._baseUrl}/lookup/${fieldKey}/admin`);
  }

  createLookupOption(request: CreateRLLookupOptionRequest): Observable<RLLookupOption> {
    return this._http.post<RLLookupOption>(`${this._baseUrl}/lookup`, request);
  }

  updateLookupOption(id: number, request: UpdateRLLookupOptionRequest): Observable<RLLookupOption> {
    return this._http.put<RLLookupOption>(`${this._baseUrl}/lookup/${id}`, request);
  }

  deleteLookupOption(id: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/lookup/${id}`);
  }

  // ─── Well search (MWL — stub in M003) ───────────────────────────────────────

  searchWells(query: string): Observable<RLWellSearchResult[]> {
    const params = new HttpParams().set('q', query);
    return this._http.get<RLWellSearchResult[]>(`${this._baseUrl}/wells/search`, { params });
  }
}
